import { Dispatch } from "@reduxjs/toolkit";
import { isRight } from "fp-ts/lib/Either";
import {
    type History,
    createBrowserHistory,
    createMemoryHistory,
} from "history";
import { NumberFromString } from "io-ts-types/lib/NumberFromString";
import { PathReporter } from "io-ts/lib/PathReporter";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { IProduct } from "../../models/catalogue";
import { IProductID, isoProductID } from "../../models/nominals";
import { IPLCProductCategorySelector } from "./models";
import { setSelectedVariant } from "./reducers";

interface IHistoryState {
    root?: IProduct;
    variant?: IProduct;
    selectedUpgradeID?: IProductID;
    categorySelectors?: readonly IPLCProductCategorySelector[];
}

const UPGRADE_ID_PARAM = "upgrade";

interface IPDPHistory {
    history: History;
    /** The PDP's own path, without the trailing `/v/<id>/` variant segment */
    basePath: string;
}

// Set-up history API lazily to avoid SSR issues
// createBrowserHistory requires a browser environment with document
let _pdpHistory: IPDPHistory | null = null;

const getPDPHistory = (): IPDPHistory => {
    if (_pdpHistory) {
        return _pdpHistory;
    }

    const window = getWindow();
    const document = getDocument();
    const isBrowser = !!window && !!document;

    if (isBrowser) {
        const baseURL = window.location.pathname.replace(/\/(v\/\d+\/?)$/, "/");
        console.log(`[Configurator] Setting history base URL to ${baseURL}`);
        _pdpHistory = {
            history: createBrowserHistory({ window }),
            basePath: baseURL.replace(/\/$/, ""),
        };
    } else {
        _pdpHistory = {
            history: createMemoryHistory({ initialEntries: ["/"] }),
            basePath: "",
        };
    }

    return _pdpHistory;
};

/**
 * Path of the current location relative to the PDP, e.g. `/v/1234/` or `/`.
 */
const getPDPRelativePath = ({ history, basePath }: IPDPHistory): string => {
    const { pathname } = history.location;
    if (pathname === basePath) {
        return "/";
    }
    return pathname.startsWith(`${basePath}/`)
        ? pathname.slice(basePath.length)
        : pathname;
};

/**
 * React to history change events
 */
export const startHistoryListener = (dispatch: Dispatch): void => {
    getPDPHistory().history.listen(({ action, location }): void => {
        console.info(
            `[Configurator] Received history update: ${action} ${location.pathname}${location.search}${location.hash}`,
        );
        // Update Redux to match the new history location
        const state = location.state as IHistoryState | null;
        if (state && state.root && state.variant) {
            dispatch(
                setSelectedVariant({
                    root: state.root,
                    variant: state.variant,
                    categorySelectors: state.categorySelectors,
                }),
            );
        }
    });
};

/**
 * Update the PDP Page's URL to include the ID of the currently selected Variant. This is to allow
 * linking to a particular variant of a product.
 */
export const pushPDPVariantURLChange = (
    root: IProduct | null,
    variant: IProduct | null,
    selectedUpgradeID: IProductID | null,
    categorySelectors?: readonly IPLCProductCategorySelector[],
): void => {
    const newPath = variant ? `/v/${variant.id}/` : "/";
    const window = getWindow();
    const newURLParams = new URLSearchParams(window?.location.search ?? "");
    if (selectedUpgradeID) {
        newURLParams.set(UPGRADE_ID_PARAM, `${selectedUpgradeID}`);
    } else {
        newURLParams.delete(UPGRADE_ID_PARAM);
    }
    const newQueryString = newURLParams.toString();
    const newSearch = newQueryString ? `?${newQueryString}` : "";
    const historyState: IHistoryState = {
        root: root || undefined,
        variant: variant || undefined,
        selectedUpgradeID: selectedUpgradeID || undefined,
        categorySelectors: categorySelectors,
    };
    const pdpHistory = getPDPHistory();
    const { history, basePath } = pdpHistory;
    const currentPath = getPDPRelativePath(pdpHistory);
    const entry = {
        pathname: `${basePath}${newPath}`,
        search: newSearch,
    };
    const pathMatchesExpected = newPath === currentPath;
    const searchMatchesExpected = newSearch === history.location.search;
    // If nothing has changed, don't do anything
    if (
        pathMatchesExpected &&
        searchMatchesExpected &&
        history.location.state !== null
    ) {
        return;
    }
    // If this is just a state change, replace the current state. Otherwise,
    // push it onto the history stack.
    if ((pathMatchesExpected && searchMatchesExpected) || currentPath === "/") {
        history.replace(entry, historyState);
    } else {
        history.push(entry, historyState);
    }
};

/**
 * Get the configurator selected upgrade ID from the query string
 */
export const getSelectedUpgradeIDFromURL = (): IProductID | null => {
    const window = getWindow();
    const urlParams = new URLSearchParams(window?.location.search ?? "");
    const rawUpgradeID = urlParams.get(UPGRADE_ID_PARAM);
    if (!rawUpgradeID) {
        return null;
    }
    const maybeUpgradeID = NumberFromString.decode(rawUpgradeID);
    if (isRight(maybeUpgradeID)) {
        return isoProductID.wrap(maybeUpgradeID.right);
    }
    console.log(PathReporter.report(maybeUpgradeID).join("\n\n"));
    return null;
};
